import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { NeonRichTextEditor } from '@ps-refarch-ux/neon';
import {
  readNeonRichTextEditorIsValid,
  type NeonRichTextEditorHost,
} from '@/utils/neon-rich-text-editor-validity';
import styles from './description-field.module.scss';

export interface DescriptionFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  label: string;
  /** Shown as Neon `dataHelperText` below the label (when supported by the widget). */
  helperText?: string;
  /** Mirrors Neon’s internal validation (e.g. max length); use to disable Save when invalid. */
  onNeonValidityChange?: (isValid: boolean) => void;
  /**
   * When set, passed to Neon `dataMaxLength` / SunEditor `maxCharCount` (HTML string length).
   * Omit for rich-text fields where formatting markup should not count against the limit (POS parity).
   */
  maxHtmlLength?: number;
  /** SunEditor `minHeight` inside `sunEditorOptions`. */
  editorMinHeight?: string;
}

export function DescriptionField({
  id,
  value,
  onChange,
  label,
  helperText,
  onNeonValidityChange,
  maxHtmlLength,
  editorMinHeight = '250px',
}: DescriptionFieldProps): ReactElement {
  const sunEditorOptions = useMemo(
    () => ({ height: 'auto' as const, minHeight: editorMinHeight }),
    [editorMinHeight]
  );
  const hostRef = useRef<NeonRichTextEditorHost | null>(null);

  const emitNeonValidity = useCallback(() => {
    if (!onNeonValidityChange) {
      return;
    }
    queueMicrotask(() => {
      onNeonValidityChange(readNeonRichTextEditorIsValid(hostRef.current));
    });
  }, [onNeonValidityChange]);

  useEffect(() => {
    if (!onNeonValidityChange) {
      return;
    }
    const t = window.setTimeout(() => {
      onNeonValidityChange(readNeonRichTextEditorIsValid(hostRef.current));
    }, 0);
    return () => window.clearTimeout(t);
  }, [value, onNeonValidityChange]);

  return (
    <div className={styles.editorHost}>
      <NeonRichTextEditor
        ref={hostRef}
        id={id}
        dataLabelText={label}
        dataHelperText={helperText}
        modelValue={value}
        modelValueChange={(v: string | undefined) => {
          onChange(v ?? '');
          emitNeonValidity();
        }}
        sunEditorOptions={sunEditorOptions}
        {...(typeof maxHtmlLength === 'number' ? { dataMaxLength: maxHtmlLength } : {})}
      />
    </div>
  );
}
